/*
 *
 * Accessibility layer for Boost AI Search & Discovery
 * 
 */

(function () {
  'use strict';

  var SEL_WRAP   = '.boost-sd__show-limit-list';
  var SEL_BTN    = '.boost-sd__show-limit-list-button';
  var SEL_LIST   = '.boost-sd__show-limit-list-list';
  var SEL_OPTION = '.boost-sd__show-limit-list-option';
  var SEL_APP    = '.shopify-app-block';
  var CLS_OPEN   = 'boost-sd__show-limit-list-button--active';

  var BOOT_INTERVAL_MS = 250;
  var BOOT_TIMEOUT_MS  = 20000;
  var RESTORE_FOCUS_MS = 10000;

  var classObserver = null;
  var restoreFocus  = false;
  var restoreTimer  = null;
  var tabbingOut    = false;

  function getParts(wrap) {
    if (!wrap) return null;
    var btn  = wrap.querySelector(SEL_BTN);
    var list = wrap.querySelector(SEL_LIST);
    if (!btn || !list) return null;
    return {
      wrap: wrap,
      btn: btn,
      list: list,
      options: Array.prototype.slice.call(list.querySelectorAll(SEL_OPTION))
    };
  }

  function isOpen(btn) {
    return btn.classList.contains(CLS_OPEN);
  }

  function syncExpanded(btn) {
    btn.setAttribute('aria-expanded', isOpen(btn) ? 'true' : 'false');
  }

  function syncSelected(parts) {
    var match = parts.btn.textContent.match(/\d+/);
    if (!match) return;
    var current = parseInt(match[0], 10);
    parts.options.forEach(function (opt) {
      var value = parseInt(opt.textContent, 10);
      opt.setAttribute('aria-selected', value === current ? 'true' : 'false');
    });
  }

  function selectedIndex(parts) {
    for (var i = 0; i < parts.options.length; i++) {
      if (parts.options[i].getAttribute('aria-selected') === 'true') return i;
    }
    return 0;
  }

  function focusOption(parts, index) {
    var count = parts.options.length;
    if (!count) return;
    var i = Math.max(0, Math.min(count - 1, index));
    parts.options[i].focus();
  }

  // Boost may open the list a tick after the click, so wait for it.
  function whenOpen(btn, callback, tries) {
    if (isOpen(btn)) {
      callback();
      return;
    }
    if (tries <= 0) return;
    setTimeout(function () { whenOpen(btn, callback, tries - 1); }, 50);
  }

  function closeList(parts, returnFocus) {
    if (isOpen(parts.btn)) parts.btn.click();
    if (returnFocus) parts.btn.focus();
  }

  function onKeydown(e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var wrap = target.closest(SEL_WRAP);
    if (!wrap) return;
    var parts = getParts(wrap);
    if (!parts) return;

    if (target === parts.btn) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (isOpen(parts.btn)) {
          focusOption(parts, selectedIndex(parts));
        } else {
          parts.btn.click(); // the click handler below moves focus into the list
        }
      } else if (e.key === 'Escape' && isOpen(parts.btn)) {
        e.preventDefault();
        closeList(parts, false);
      }
      return;
    }

    var index = parts.options.indexOf(target);
    if (index === -1) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusOption(parts, index + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusOption(parts, index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusOption(parts, 0);
        break;
      case 'End':
        e.preventDefault();
        focusOption(parts, parts.options.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        restoreFocus = true;
        clearTimeout(restoreTimer);
        restoreTimer = setTimeout(function () { restoreFocus = false; }, RESTORE_FOCUS_MS);
        target.click();
        break;
      case 'Escape':
        e.preventDefault();
        closeList(parts, true);
        break;
      case 'Tab':
        tabbingOut = true;
        break;
    }
  }

  // A click with detail 0 comes from the keyboard (Enter/Space on the button)
  // or assistive technology, so move focus into the list once it is open.
  function onClick(e) {
    var target = e.target;
    if (!target || !target.closest || e.detail !== 0) return;
    var btn = target.closest(SEL_BTN);
    if (!btn) return;
    setTimeout(function () {
      whenOpen(btn, function () {
        // Don't steal focus if the user has already moved on.
        if (document.activeElement !== btn) return;
        var parts = getParts(btn.closest(SEL_WRAP));
        if (parts) focusOption(parts, selectedIndex(parts));
      }, 6);
    }, 0);
  }

  // Close the list when keyboard focus leaves the control (Boost only closes
  // on outside clicks).
  function onFocusout(e) {
    if (!tabbingOut) return;
    var target = e.target;
    if (!target || !target.closest) return;
    var wrap = target.closest(SEL_WRAP);
    if (!wrap) return;
    if (e.relatedTarget && wrap.contains(e.relatedTarget)) return;
    tabbingOut = false;
    var parts = getParts(wrap);
    if (parts) closeList(parts, false);
  }

  function onMousedown() {
    tabbingOut = false;
  }

  function attach() {
    var parts = getParts(document.querySelector(SEL_WRAP));
    if (!parts) return;

    syncSelected(parts);
    syncExpanded(parts.btn);

    // Watches only the small control subtree for Boost's open/close class
    // toggle. We only ever write aria-* attributes, never class, so this
    // cannot re-trigger itself.
    if (classObserver) classObserver.disconnect();
    if (typeof MutationObserver === 'function') {
      classObserver = new MutationObserver(function () { syncExpanded(parts.btn); });
      classObserver.observe(parts.wrap, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true
      });
    }
  }

  function onRender() {
    attach();
    if (restoreFocus) {
      restoreFocus = false;
      var btn = document.querySelector(SEL_WRAP + ' ' + SEL_BTN);
      if (btn) btn.focus();
    }
  }

  function init(wrap) {
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onClick);
    document.addEventListener('focusout', onFocusout);
    document.addEventListener('mousedown', onMousedown);

    attach();

    var app = wrap.closest(SEL_APP);
    if (app && typeof MutationObserver === 'function') {
      new MutationObserver(onRender).observe(app, { childList: true }); // see note 3
    }
  }

  // Boost may render the toolbar client-side, well after this script runs, so
  // wait (bounded) for it to appear.
  function start() {
    var elapsed = 0;
    (function wait() {
      var wrap = document.querySelector(SEL_WRAP);
      if (wrap) {
        init(wrap);
        return;
      }
      elapsed += BOOT_INTERVAL_MS;
      if (elapsed < BOOT_TIMEOUT_MS) setTimeout(wait, BOOT_INTERVAL_MS);
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

(function () {
  'use strict';

  var SEL_BTN = '.boost-sd__filter-option-viewmorebtn';
  var SEL_APP = '.shopify-app-block';

  var BOOT_INTERVAL_MS = 250;
  var BOOT_TIMEOUT_MS  = 20000;

  function syncLabel(btn) {
    var label = btn.getAttribute('aria-label');
    if (!label) return;
    var verb = /less/i.test(btn.textContent) ? 'less' : 'more';
    var next = label.replace(/^View (more|less)/i, 'View ' + verb);
    if (next !== label) btn.setAttribute('aria-label', next);
  }

  function syncAll() {
    Array.prototype.forEach.call(document.querySelectorAll(SEL_BTN), syncLabel);
  }

  function onClick(e) {
    var target = e.target;
    if (!target || !target.closest || !target.closest(SEL_BTN)) return;
    setTimeout(syncAll, 0);
    setTimeout(syncAll, 150);
  }

  function init(btn) {
    document.addEventListener('click', onClick);
    syncAll();

    var app = btn.closest(SEL_APP);
    if (app && typeof MutationObserver === 'function') {
      new MutationObserver(syncAll).observe(app, { childList: true });
    }
  }

  function start() {
    var elapsed = 0;
    (function wait() {
      var btn = document.querySelector(SEL_BTN);
      if (btn) {
        init(btn);
        return;
      }
      elapsed += BOOT_INTERVAL_MS;
      if (elapsed < BOOT_TIMEOUT_MS) setTimeout(wait, BOOT_INTERVAL_MS);
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();